import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { GridReadyEvent, RowDataUpdatedEvent, ValueGetterParams } from 'ag-grid-community';
import { ColDef, GridApi, GridOptions } from 'ag-grid-enterprise';
import { ToastrManager } from '@niceltd/cxone-components/toastr';
import moment from 'moment';
import { CustomNoRowsOverlayComponent } from 'src/app/cxone/admin/shared/custom-no-rows-overlay/custom-no-rows-overlay.component';
import { CxoneHighlightSearchCellRendererComponent } from 'src/app/reusables/ag-grid-components/cxone-highlight-search-cell-renderer/cxone-highlight-search-cell-renderer.component';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { FailedImportObj } from '../../cxone-etl-data-feed/cxone-etl-data-feed.model';
import { CxoneEtlDataFeedService } from '../../cxone-etl-data-feed/cxone-etl-data-feed.service';
import { ActionCellComponent } from './action-cell/action-cell.component';

@Component({
    selector: 'cv-failed-import-report',
    templateUrl: './failed-import-report.component.html',
    styleUrls: ['./failed-import-report.component.scss']
})
export class FailedImportReportComponent implements OnInit {
    public pageHeader: string;
    public subHeader: string;
    public failedImportGridHeaders: Array<ColDef>;
    public failedImportLabel: string;
    public actionButton: string;
    public gridApi: GridApi;
    public searchInput = '';
    public searchPlaceholderText: string = '';
    public isFilterable: boolean = false;
    public gridData: FailedImportObj[];
    public showSpinner: boolean;
    public isLocalizationSectionLoaded: boolean;

    @Input() pageTitle: string;
    @Input() apiType: string;
    @Output() backAction: EventEmitter<void> = new EventEmitter();

    failedImportGridOptions: GridOptions = {
        onRowDataUpdated: (params: RowDataUpdatedEvent) => {
            this.gridApi.sizeColumnsToFit();
            this.failedImportLabel = this.getTotalImportsText(params.api.getDisplayedRowCount());
        },
        onGridReady: (event: GridReadyEvent) => this.onGridReady(event),
        noRowsOverlayComponent: 'CustomNoRowsOverlayComponent',
        noRowsOverlayComponentParams: {
            noRowsOverlayTextFunc: () => this.searchInput !== '' ?
                this.getLocalizationString("No_Result_Found", "No result found")
                : this.getLocalizationString("No_Failed_Reports", "No failed reports"),
            noRowsOverlayIconPathFunc: () => {
                const path = this.searchInput !== '' ?
                    '../App_Themes/CXone/icons/no-results.svg#no-results'
                    : '../App_Themes/CXone/icons/empty-state.svg#empty-state';
                return path;
            }
        },
        components: {
            CustomNoRowsOverlayComponent,
            cxoneHighlightSearchRenderer: CxoneHighlightSearchCellRendererComponent
        },
        onFilterChanged: () => {
            this.gridApi.redrawRows();
            this.setRowCountLabel();
        },
        isExternalFilterPresent: () => true,
        doesExternalFilterPass: (node) => {
            const result = this.agGridHelperService.doesExternalFilterPassIncludeHidden(node, this.searchInput);
            return result;
        },
        context: this,
        scrollbarWidth: 8
    };

    constructor(
        private agGridHelperService: AgGridHelperService,
        private cvLocalizationService: CVLocalizationService,
        private cxoneEtlDataFeedService: CxoneEtlDataFeedService,
        private toastrManager: ToastrManager
    ) { }

    async ngOnInit(): Promise<void> {
        this.showSpinner = true;
        await this.loadLocalization();
        this.setColumnDefination();
        this.isLocalizationSectionLoaded = true;

        if (this.apiType && this.apiType !== '') {
            try {
                const response = await this.cxoneEtlDataFeedService.loadFailedImports(this.apiType);
                this.gridData = response;
                this.showSpinner = false;
            } catch (error) {
                this.gridData = [];
                this.toastrManager
                    .showToaster('error',
                        this.getLocalizationString('Internal_Server_Error', 'Internal server error. Try again.'));
                this.showSpinner = false;
            }
        }
    }

    public async loadLocalization() {
        try {
            await this.cvLocalizationService.GetSection('Failed_Reports');
        }
        catch {
            console.log('Failed to load localization for Failed_Reports');
        }
        finally {
            this.pageHeader = this.getLocalizationString("Failed_Imports", "Failed Imports");
            this.subHeader = "(" + this.getLocalizationString("Past_2_weeks", "Past 2 weeks") + ")";
            this.searchPlaceholderText = this.getLocalizationString("Search_reports", "Search reports");
        }
    }

    public setColumnDefination() {
        if (this.apiType && this.apiType !== '') {
            this.failedImportGridHeaders = [
                {
                    headerName: this.getLocalizationString("Source", "Source"),
                    field: 'SourceSystem',
                    cellRenderer: 'cxoneHighlightSearchRenderer',
                    sortable: true,
                    resizable: true,
                    hide: this.apiType !== 'raw-data' ? true : false
                }, {
                    headerName: this.getLocalizationString("Feed_Name", "Feed Name"),
                    field: 'FeedName',
                    cellRenderer: 'cxoneHighlightSearchRenderer',
                    sortable: true,
                    resizable: true,
                    hide: this.apiType !== 'raw-data' ? true : false
                }, {
                    headerName: this.getLocalizationString("Reporting_Set", "Reporting set"),
                    field: 'ReportingSetID',
                    cellRenderer: 'cxoneHighlightSearchRenderer',
                    sortable: true,
                    resizable: true,
                    hide: this.apiType !== 'reporting-set' ? true : false
                }, {
                    headerName: this.getLocalizationString("Import_Id", "Import Id"),
                    field: 'ImportId',
                    cellRenderer: 'cxoneHighlightSearchRenderer',
                    sortable: false,
                    resizable: true,
                },
                {
                    headerName: this.getLocalizationString("Start_Date", "Start Date"),
                    field: 'ProcessStartTime',
                    cellRenderer: 'cxoneHighlightSearchRenderer',
                    valueGetter: (params: ValueGetterParams) => {
                        if (params.data == null) {
                            return null;
                        } else {
                            return params.data?.ProcessStartTime ?
                                moment(params.data?.ProcessStartTime).format('MMM D, YYYY h:mm:ss A') : 'NA';
                        }
                    },
                    sortable: true,
                    sort: 'desc',
                    resizable: true,
                }, {
                    headerName: this.getLocalizationString("Reason", "Reason"),
                    field: 'Reason',
                    cellRenderer: ActionCellComponent,
                    valueGetter: (params: ValueGetterParams) => {
                        if (params.data == null) {
                            return null;
                        } else {
                            if (this.apiType === 'raw-data') {
                                params.data.Title = params.data?.SourceSystem + '|' + params.data?.FeedName;
                            } else if (this.apiType === 'reporting-set') {
                                params.data.Title = params.data.ReportingSetID;
                            }
                            return params.data;
                        }
                    },
                    filterValueGetter: () => null,
                    sortable: false,
                    resizable: true,
                }
            ];
        }
    }

    public onGridReady(event: GridReadyEvent) {
        this.gridApi = event.api;
    }

    public onSearchCriteriaChange = (searchCriteria: string) => {
        this.searchInput = searchCriteria;
        if (this.gridApi) {
            this.gridApi.setGridOption('quickFilterText', searchCriteria);
            this.gridApi.onFilterChanged();
            if (this.gridApi?.getDisplayedRowCount() === 0)
                this.gridApi.showNoRowsOverlay();
            else
                this.gridApi?.hideOverlay();
        }
    };

    public getLocalizationString(key: string, defaultValue: string): string {
        return this.cvLocalizationService.getCachedText("Failed_Reports", key, defaultValue);
    }

    public getTotalImportsText(count: number): string {
        const key: string = count === 1 ? 'Total_Imports_Singular' : 'Total_Imports_Plural';
        const defaultValue: string = count === 1 ? '{0} import' : '{0} imports';
        return this.cvLocalizationService.Format(this.getLocalizationString(key, defaultValue).toLowerCase(), count);
    }

    public setRowCountLabel() {
        let count = 0;
        this.gridApi.forEachNodeAfterFilter((node) => {
            count++;
        });
        this.failedImportLabel = this.getTotalImportsText(count);
    }

    public getFilterText(): string {
        return this.searchInput.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    }

    public onBack() {
        this.backAction.emit(null);
    }
}
